import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpErrorResponse,
  HttpBackend,
  HttpClient
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take, map } from 'rxjs/operators';
import { AuthService } from '../services/Motor_leasing/auth.service';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const httpBackend = inject(HttpBackend);
  const httpNoInterceptor = new HttpClient(httpBackend);

  const token = localStorage.getItem('token');
  const refreshToken = localStorage.getItem('refreshToken');

  const sub = getSubdomain();
  const subdomain = (sub === 'localhost' || sub === 'thiqatech' || sub === 'ainalthiqah') ? '' : sub;

  if (req.url.includes('/refresh-token')) {
    return next(req);
  }

  const expiry = parseInt(localStorage.getItem('tokenExpiry') || '0', 10);
  const now = Math.floor(Date.now() / 1000);
  const willExpireSoon = expiry && expiry - now < 10;

  let headers: Record<string, string> = { 'X-Tenant': subdomain };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const authReq = req.clone({ setHeaders: headers });

  if (token && refreshToken && willExpireSoon && !isRefreshing) {
    // Trigger proactive refresh before request
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return refreshAccessToken(httpNoInterceptor, subdomain).pipe(
      switchMap((newToken: string) => {
        isRefreshing = false;
        refreshTokenSubject.next(newToken);

        return next(
          req.clone({
            setHeaders: {
              Authorization: `Bearer ${newToken}`,
              'X-Tenant': subdomain
            }
          })
        );
      }),
      catchError(err => {
        console.error('Proactive refresh failed, logging out...');
        isRefreshing = false;
        redirectToLogin(authService, router, subdomain);
        return throwError(() => err);
      })
    );
  }

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && refreshToken) {
        if (!isRefreshing) {
          isRefreshing = true;
          refreshTokenSubject.next(null);

          return refreshAccessToken(httpNoInterceptor, subdomain).pipe(
            switchMap((newToken: string) => {
              isRefreshing = false;
              refreshTokenSubject.next(newToken);

              return next(
                req.clone({
                  setHeaders: {
                    Authorization: `Bearer ${newToken}`,
                    'X-Tenant': subdomain
                  }
                })
              );
            }),
            catchError(err => {
              isRefreshing = false;
              redirectToLogin(authService, router, subdomain);
              return throwError(() => err);
            })
          );
        } else {
          return refreshTokenSubject.pipe(
            filter(token => token !== null),
            take(1),
            switchMap((newToken: string | null) => {
              return next(
                req.clone({
                  setHeaders: {
                    Authorization: `Bearer ${newToken}`,
                    'X-Tenant': subdomain
                  }
                })
              );
            })
          );
        }
      }

      return throwError(() => error);
    })
  );
};

function getSubdomain(): string {
  const host = window.location.hostname;
  return host.split('.')[0] || 'default';
}

/**
 * Determines where the user should be sent on session expiry, based on the
 * subdomain and, failing that, the last known user role. Mirrors the
 * subdomain/role branching used in AppComponent so a lessor/lessee/admin
 * session that expires mid-page lands back on its own login screen instead
 * of the app root.
 */
function resolveLoginRedirect(subdomain: string): { path: string; userType: 'lessor' | 'lessee' | 'admin' } | null {
  if (subdomain === 'admin') {
    return { path: '/admin-login', userType: 'admin' };
  }

  try {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;

    if (user?.role === 'LessorAdmin' || user?.role === 'LessorProxyAdmin') {
      return { path: '/lessor-login', userType: 'lessor' };
    }

    if (user?.role === 'Lessee') {
      return { path: '/lessee-login', userType: 'lessee' };
    }
  } catch (e) {
    console.error('Failed to parse stored user during logout redirect', e);
  }

  return null;
}

function redirectToLogin(authService: AuthService, router: Router, subdomain: string): void {
  const redirect = resolveLoginRedirect(subdomain);
  if (!redirect) {
    return;
  }

  const { path, userType } = redirect;
  authService.logout(userType);
  router.navigate([path]);
}

function refreshAccessToken(http: HttpClient, subdomain: string): Observable<string> {
  const accessToken = localStorage.getItem('token');
  const refreshToken = localStorage.getItem('refreshToken');

  return http
    .post<{
      data: any; accessToken: string; refreshToken: string
}>(
      'https://api.ainalthiqah.shop/api/refresh-token',
      { accessToken, refreshToken },
      { headers: { 'X-Tenant': subdomain } }
    )
    .pipe(
      map(response => {
        if (response.data.accessToken && response.data.refreshToken) {
          localStorage.setItem('token', response.data.accessToken);
          localStorage.setItem('refreshToken', response.data.refreshToken);
          const decoded = decodeJwt(response.accessToken);
          if (decoded?.exp) {
            localStorage.setItem('tokenExpiry', decoded.exp.toString());
          }

          return response.data.accessToken;
        } else {
          throw new Error('Invalid token refresh response');
        }
      })
    );
}

function decodeJwt(token: string): any {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch (e) {
    console.error('Failed to decode JWT', e);
    return null;
  }
}